// src/components/OcrBatchEnrich.jsx
//
// Enrichissement OCR en batch depuis CollectionDetail
// — Parcourt toutes les fiches de la collection avec docs PDF mais champs vides
// — Appelle l'API OCR pour chaque fiche
// — Met à jour les champs manquants en base
//
// Branchement dans CollectionDetail.jsx :
//   import OcrBatchEnrich from "../components/OcrBatchEnrich";
//   // Ajouter un état :
//   const [ocrBatchOpen, setOcrBatchOpen] = useState(false);
//   // Bouton (visible pour isExpert) :
//   {isExpert && (
//     <button onClick={() => setOcrBatchOpen(true)}>OCR Batch</button>
//   )}
//   // Modal :
//   {ocrBatchOpen && (
//     <OcrBatchEnrich
//       collectionId={collection.id}
//       clientId={collection.isShared ? sharedOwnerId : user.id}
//       onClose={() => setOcrBatchOpen(false)}
//     />
//   )}

import React, { useState, useEffect, useRef } from "react";
import { X, Sparkles, Loader2, CheckCircle2, AlertCircle, ChevronRight } from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { VISION_API_URL } from "../config/domains";

const OCR_API = `${VISION_API_URL}/analyze-document`;

const FIELDS_MAP = {
  year:             "year",
  dimensions:       "dimensions",
  medium:           "medium",
  acquisition_price: "price",
  title:            "title",
  artist:           "artist",
};

function parseDocuments(docs) {
  if (!docs) return [];
  try {
    const arr = typeof docs === "string" ? JSON.parse(docs) : docs;
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function hasMissingFields(artwork) {
  return !artwork.year || !artwork.medium || !artwork.dimensions;
}

export default function OcrBatchEnrich({ collectionId, clientId, onClose }) {
  const [artworks,   setArtworks]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [running,    setRunning]    = useState(false);
  const [results,    setResults]    = useState([]); // [{id, inventory_number, status, fields}]
  const [current,    setCurrent]    = useState(null);
  const [done,       setDone]       = useState(false);
  const cancelRef = useRef(false);

  // Charger les fiches avec docs mais champs manquants
  useEffect(() => {
    if (!collectionId) return;
    supabase
      .from("artworks")
      .select("id, title, artist, year, medium, dimensions, acquisition_price, inventory_number, documents, images")
      .eq("collection_id", collectionId)
      .eq("client_id", clientId)
      .then(({ data }) => {
        const eligible = (data || []).filter(a => {
          const docs = parseDocuments(a.documents);
          const hasPdf = docs.some(d => (d.file || d.url) && /\.pdf/i.test(d.file || d.url || ""));
          return hasPdf && hasMissingFields(a);
        });
        setArtworks(eligible);
        setLoading(false);
      });
  }, [collectionId, clientId]);

  // Lancer le batch
  const handleStart = async () => {
    setRunning(true);
    cancelRef.current = false;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const batchResults = [];

    for (const artwork of artworks) {
      if (cancelRef.current) break;
      setCurrent(artwork.inventory_number || artwork.title || artwork.id);

      try {
        const docs = parseDocuments(artwork.documents);
        const pdf = docs.find(d => /\.pdf/i.test(d.file || d.url || ""));
        if (!pdf) continue;

        const pdfUrl = pdf.file || pdf.url;
        const res = await fetch(pdfUrl);
        const blob = await res.blob();
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const ocrRes = await fetch(OCR_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            file: b64,
            document_type: null,
            client_id: clientId || null,
          }),
        });

        if (!ocrRes.ok) throw new Error(`OCR ${ocrRes.status}`);
        const result = await ocrRes.json();
        const data = result.extracted_data || result.artworks?.[0] || {};

        // Construire le patch — seulement les champs vides
        const patch = {};
        for (const [artworkField, ocrField] of Object.entries(FIELDS_MAP)) {
          const val = data[ocrField];
          if (val && String(val).trim() && !artwork[artworkField]) {
            patch[artworkField] = String(val).trim();
          }
        }

        if (Object.keys(patch).length > 0) {
          await supabase.from("artworks").update(patch).eq("id", artwork.id);
          batchResults.push({
            id: artwork.id,
            ref: artwork.inventory_number || artwork.title,
            status: "updated",
            fields: Object.keys(patch),
          });
        } else {
          batchResults.push({
            id: artwork.id,
            ref: artwork.inventory_number || artwork.title,
            status: "no_data",
            fields: [],
          });
        }

        // Pause pour ne pas surcharger l'API
        await new Promise(r => setTimeout(r, 800));

      } catch (e) {
        batchResults.push({
          id: artwork.id,
          ref: artwork.inventory_number || artwork.title,
          status: "error",
          fields: [],
          error: e.message,
        });
      }

      setResults([...batchResults]);
    }

    setCurrent(null);
    setRunning(false);
    setDone(true);
  };

  const updated  = results.filter(r => r.status === "updated").length;
  const errors   = results.filter(r => r.status === "error").length;
  const no_data  = results.filter(r => r.status === "no_data").length;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget && !running) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: "12px 12px 0 0", width: "min(540px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column", fontFamily: "Georgia, serif" }}>

        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0ede8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Sparkles size={18} color="#c9a961" />
            <div>
              <p style={{ margin: 0, fontSize: 16, color: "#1a1a1a" }}>Enrichissement OCR batch</p>
              <p style={{ margin: 0, fontSize: 12, color: "#6b6b6b", marginTop: 2 }}>
                {loading ? "Analyse des fiches…" : `${artworks.length} fiche${artworks.length > 1 ? "s" : ""} éligible${artworks.length > 1 ? "s" : ""} (docs PDF + champs manquants)`}
              </p>
            </div>
          </div>
          {!running && <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} color="#6b6b6b" /></button>}
        </div>

        {/* Corps */}
        <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>

          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#6b6b6b", fontSize: 14 }}>
              <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
              Chargement des fiches…
            </div>
          )}

          {!loading && !running && !done && artworks.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#6b6b6b" }}>
              <p style={{ fontSize: 15 }}>Aucune fiche éligible trouvée.</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Toutes les fiches avec PDFs ont déjà leurs champs renseignés.</p>
            </div>
          )}

          {!loading && !running && !done && artworks.length > 0 && (
            <div style={{ display: "grid", gap: 8 }}>
              <p style={{ fontSize: 13, color: "#6b6b6b", margin: "0 0 8px" }}>
                Les fiches suivantes seront analysées. Seuls les champs <strong>vides</strong> seront mis à jour.
              </p>
              {artworks.map(a => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#fafaf8", border: "1px solid #e0ddd8", borderRadius: 8 }}>
                  <span style={{ fontSize: 13, fontFamily: "system-ui", fontWeight: 600, color: "#c9a961" }}>
                    {a.inventory_number || "—"}
                  </span>
                  <span style={{ fontSize: 13, color: "#6b6b6b" }}>
                    {[!a.year && "année", !a.medium && "medium", !a.dimensions && "dimensions"].filter(Boolean).join(", ")}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* En cours */}
          {running && (
            <div style={{ display: "grid", gap: 8 }}>
              {current && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "#fdf9f0", border: "1px solid #c9a961", borderRadius: 8 }}>
                  <Loader2 size={14} color="#c9a961" style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: "#1a1a1a" }}>Analyse de <strong>{current}</strong>…</span>
                </div>
              )}
              {results.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: r.status === "updated" ? "#f0fdf4" : r.status === "error" ? "#fef2f2" : "#fafaf8", border: `1px solid ${r.status === "updated" ? "#bbf7d0" : r.status === "error" ? "#fecaca" : "#e0ddd8"}`, borderRadius: 8 }}>
                  {r.status === "updated" ? <CheckCircle2 size={14} color="#16a34a" /> : r.status === "error" ? <AlertCircle size={14} color="#dc2626" /> : <ChevronRight size={14} color="#6b6b6b" />}
                  <span style={{ fontSize: 13, fontFamily: "system-ui", fontWeight: 600, color: "#6b6b6b", minWidth: 80 }}>{r.ref}</span>
                  <span style={{ fontSize: 12, color: "#6b6b6b" }}>
                    {r.status === "updated" ? r.fields.join(", ") : r.status === "error" ? r.error : "aucune donnée extraite"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Résultats finaux */}
          {done && (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div style={{ textAlign: "center", padding: "14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#16a34a" }}>{updated}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#16a34a" }}>enrichies</p>
                </div>
                <div style={{ textAlign: "center", padding: "14px", background: "#fafaf8", border: "1px solid #e0ddd8", borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#6b6b6b" }}>{no_data}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b6b6b" }}>sans donnée</p>
                </div>
                <div style={{ textAlign: "center", padding: "14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#dc2626" }}>{errors}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#dc2626" }}>erreurs</p>
                </div>
              </div>
              {results.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: r.status === "updated" ? "#f0fdf4" : r.status === "error" ? "#fef2f2" : "#fafaf8", border: `1px solid ${r.status === "updated" ? "#bbf7d0" : r.status === "error" ? "#fecaca" : "#e0ddd8"}`, borderRadius: 8 }}>
                  {r.status === "updated" ? <CheckCircle2 size={14} color="#16a34a" /> : r.status === "error" ? <AlertCircle size={14} color="#dc2626" /> : <ChevronRight size={14} color="#6b6b6b" />}
                  <span style={{ fontSize: 13, fontFamily: "system-ui", fontWeight: 600, color: "#6b6b6b", minWidth: 80 }}>{r.ref}</span>
                  <span style={{ fontSize: 12, color: "#6b6b6b" }}>
                    {r.status === "updated" ? r.fields.join(", ") : r.status === "error" ? r.error : "aucune donnée"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!done && (
          <div style={{ padding: "14px 20px", borderTop: "1px solid #f0ede8" }}>
            {!running ? (
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleStart}
                  disabled={loading || artworks.length === 0}
                  style={{
                    flex: 1, padding: "12px",
                    background: "#1a1a1a", color: "#fff",
                    border: "none", borderRadius: 8,
                    fontSize: 15, fontFamily: "Georgia, serif",
                    cursor: artworks.length === 0 ? "not-allowed" : "pointer",
                    opacity: artworks.length === 0 ? 0.4 : 1,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  <Sparkles size={16} />
                  Lancer l'enrichissement OCR
                </button>
                <button
                  onClick={onClose}
                  style={{ padding: "12px 20px", background: "transparent", border: "1px solid #e0ddd8", borderRadius: 8, cursor: "pointer", fontSize: 14, color: "#6b6b6b" }}
                >
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => { cancelRef.current = true; }}
                style={{ width: "100%", padding: "12px", background: "transparent", border: "1px solid #e0ddd8", borderRadius: 8, cursor: "pointer", fontSize: 14, color: "#6b6b6b" }}
              >
                Arrêter
              </button>
            )}
          </div>
        )}

        {done && (
          <div style={{ padding: "14px 20px", borderTop: "1px solid #f0ede8" }}>
            <button
              onClick={onClose}
              style={{ width: "100%", padding: "12px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontFamily: "Georgia, serif" }}
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}